import { InfoIcon } from '@sharedCR/coreDesignSystem/icons';
import { compose } from 'redux';

import { FieldInputType } from 'constants/fields';
import { FieldType } from 'constants/formGenerator';
import {
  baseStatusOptions,
  projectClassificationOptions,
  projectStatusTypeOptions,
} from 'constants/options';
import { ItemGroupAlignment, ItemLabelPlacement } from 'constants/ui';
import { getConditionalArray } from 'utils/data';
import { fillConfigFieldsWithKeys } from 'utils/loanManagement';

import { PropertyAndLoanTestIds } from '../../../../PropertyAndLoan.testIds';
import {
  customIconStyles,
  customIconWrapperStyles,
  customTitleStyles,
  customTitleWrapperStyles,
} from '../MainContent.styles';
import {
  CONDOMINIUM_COOPERATIVE_PHASE_MAX_LENGTH,
  CPM_PROJECT_ID_MAX_LENGTH,
  PROJECT_INFORMATION_MAX_NUMBER,
  PROJECT_INFORMATION_MIN_NUMBER,
  PROJECT_NAME_MAX_LENGTH,
} from '../Property/Property.constants';

export const projectConfigFields = {
  projectName: {
    fieldId: 'projectName',
    type: FieldType.INPUT,
    nameInModel: 'project.projectInfo.projectName',
    inputProps: {
      label: 'Project Name',
      disabled: false,
      required: false,
      maxLength: PROJECT_NAME_MAX_LENGTH,
      dataTestId: PropertyAndLoanTestIds.PROJECT_FORM_PROJECT_NAME_INPUT,
    },
  },
  projectStatusType: {
    fieldId: 'projectStatusType',
    type: FieldType.SELECT,
    nameInModel: 'project.projectInfo.projectStatusType',
    inputProps: {
      label: 'Project Status Type',
      disabled: false,
      required: false,
      options: projectStatusTypeOptions,
      dataTestId: PropertyAndLoanTestIds.PROJECT_FORM_PROJECT_STATUS_TYPE_SELECT,
    },
  },
  projectClassification: {
    fieldId: 'projectClassification',
    type: FieldType.SELECT,
    nameInModel: 'project.projectInfo.projectClassification',
    inputProps: {
      label: 'Project Classification',
      disabled: false,
      required: false,
      options: projectClassificationOptions,
      dataTestId: PropertyAndLoanTestIds.PROJECT_FORM_PROJECT_CLASSIFICATION_SELECT,
    },
  },
  cpmProjectId: {
    fieldId: 'cpmProjectId',
    type: FieldType.INPUT,
    nameInModel: 'project.projectInfo.cpmProjectId',
    inputProps: {
      label: 'CPM Project ID# (if any)',
      disabled: false,
      required: false,
      maxLength: CPM_PROJECT_ID_MAX_LENGTH,
      dataTestId: PropertyAndLoanTestIds.PROJECT_FORM_CPM_PROJECT_ID_INPUT,
    },
  },
  projectUnitsNumber: {
    fieldId: 'projectUnitsNumber',
    type: FieldType.INPUT,
    nameInModel: 'project.projectInfo.projectUnitsNumber',
    inputProps: {
      label: 'Number of Units (Project)',
      disabled: false,
      required: false,
      type: FieldInputType.INT,
      minValue: PROJECT_INFORMATION_MIN_NUMBER,
      maxValue: PROJECT_INFORMATION_MAX_NUMBER,
      dataTestId: PropertyAndLoanTestIds.PROJECT_FORM_PROJECT_NUMBER_OF_UNITS_INPUT,
    },
  },
  projectSoldUnitsNumber: {
    fieldId: 'projectSoldUnitsNumber',
    type: FieldType.INPUT,
    nameInModel: 'project.projectInfo.projectSoldUnitsNumber',
    inputProps: {
      label: 'Number of Units Sold',
      disabled: false,
      required: false,
      type: FieldInputType.INT,
      minValue: PROJECT_INFORMATION_MIN_NUMBER,
      maxValue: PROJECT_INFORMATION_MAX_NUMBER,
      dataTestId: PropertyAndLoanTestIds.PROJECT_FORM_NUMBER_OF_UNITS_SOLD_INPUT,
    },
  },
  estimatedRemainingLife: {
    fieldId: 'estimatedRemainingLife',
    type: FieldType.INPUT,
    nameInModel: 'project.projectInfo.estimatedRemainingLife',
    inputProps: {
      label: 'Estimated Remaining Life (Years)',
      disabled: false,
      required: false,
      type: FieldInputType.INT,
      minValue: PROJECT_INFORMATION_MIN_NUMBER,
      maxValue: PROJECT_INFORMATION_MAX_NUMBER,
      dataTestId: PropertyAndLoanTestIds.PROJECT_FORM_ESTIMATED_REMAINING_LIFE_INPUT,
    },
  },
  associationFeesName: {
    fieldId: 'associationFeesName',
    type: FieldType.INPUT,
    nameInModel: 'project.hoaInfo.associationFeesName',
    inputProps: {
      label: 'Association Name',
      disabled: false,
      required: false,
      dataTestId: PropertyAndLoanTestIds.PROJECT_FORM_SUBJECT_PROPERTY_CONDO_ASSOCIATION_NAME_INPUT,
    },
  },
  associationFeesEmail: {
    fieldId: 'associationFeesEmail',
    type: FieldType.INPUT,
    nameInModel: 'project.hoaInfo.associationFeesEmail',
    inputProps: {
      label: 'Association Email',
      disabled: false,
      required: false,
      type: FieldInputType.EMAIL,
      dataTestId:
        PropertyAndLoanTestIds.PROJECT_FORM_SUBJECT_PROPERTY_CONDO_ASSOCIATION_EMAIL_INPUT,
    },
  },
  associationFeesPhoneNumber: {
    fieldId: 'associationFeesPhoneNumber',
    type: FieldType.INPUT,
    nameInModel: 'project.hoaInfo.associationFeesPhoneNumber',
    inputProps: {
      label: 'Association Phone',
      disabled: false,
      required: false,
      type: FieldInputType.TEL_EXT,
      dataTestId:
        PropertyAndLoanTestIds.PROJECT_FORM_SUBJECT_PROPERTY_CONDO_ASSOCIATION_PHONE_INPUT,
    },
  },
  condominiumCooperativePhase: {
    fieldId: 'condominiumCooperativePhase',
    type: FieldType.INPUT,
    nameInModel: 'project.projectInfo.condominiumCooperativePhase',
    inputProps: {
      label: 'Condominium/Cooperative Phase',
      disabled: false,
      required: false,
      maxLength: CONDOMINIUM_COOPERATIVE_PHASE_MAX_LENGTH,
    },
  },
  condominiumCooperativeSpotLot: {
    fieldId: 'condominiumCooperativeSpotLot',
    type: FieldType.RADIO,
    nameInModel: 'project.projectInfo.condominiumCooperativeSpotLot',
    inputProps: {
      labelPlacement: ItemLabelPlacement.END,
      align: ItemGroupAlignment.HORIZONTAL,
      radioGroupAlign: ItemGroupAlignment.HORIZONTAL,
      options: baseStatusOptions,
      label: 'Spot Lot',
      disabled: false,
      required: false,
      variant: 'outlined',
      columns: 4,
    },
  },
};

export const getProjectConfig = compose(fillConfigFieldsWithKeys, ({ isCondoOrCoOp }) => ({
  groups: [
    {
      type: FieldType.GROUP,
      fields: [
        projectConfigFields.projectName,
        projectConfigFields.projectStatusType,
        projectConfigFields.projectClassification,
        projectConfigFields.cpmProjectId,
        projectConfigFields.projectUnitsNumber,
        projectConfigFields.projectSoldUnitsNumber,
        projectConfigFields.estimatedRemainingLife,
      ],
      key: 'project-info',
      title: {
        label: 'Project Info',
        icon: InfoIcon,
        titleStyles: customTitleStyles,
        wrapperStyles: customTitleWrapperStyles,
        iconWrapperStyles: customIconWrapperStyles,
        iconStyles: customIconStyles,
      },
    },
    ...getConditionalArray(isCondoOrCoOp, () => [
      {
        type: FieldType.GROUP,
        fields: [
          projectConfigFields.condominiumCooperativePhase,
          projectConfigFields.condominiumCooperativeSpotLot,
        ],
        key: 'condo-coop-info',
        title: {
          label: 'Condominium/Cooperative Info',
          icon: InfoIcon,
          titleStyles: customTitleStyles,
          wrapperStyles: { ...customTitleWrapperStyles, marginTop: '24px' },
          iconWrapperStyles: customIconWrapperStyles,
          iconStyles: customIconStyles,
        },
      },
    ]),
    {
      type: FieldType.GROUP,
      fields: [
        projectConfigFields.associationFeesName,
        projectConfigFields.associationFeesEmail,
        projectConfigFields.associationFeesPhoneNumber,
      ],
      key: 'hoa-info',
      title: {
        label: 'HOA Info',
        icon: InfoIcon,
        titleStyles: customTitleStyles,
        wrapperStyles: { ...customTitleWrapperStyles, marginTop: '24px' },
        iconWrapperStyles: customIconWrapperStyles,
        iconStyles: customIconStyles,
      },
    },
  ],
}));
